import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUserRole } from '../context/UserRoleContext';
import useCurrentUser from '../hooks/useCurrentUser';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { MarbleLoader } from '../components/ui/LoadingStates';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { firestore } from '../database/firebase';

const AuthCheck = ({ children }) => {
  const { currentUser, loading: authLoading } = useAuth();
  const { userRole, loading: roleLoading } = useUserRole();
  const { user: currentUserData, loading: userLoading } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();

  const publicRoutes = ['/', '/landing', '/account-not-activated'];

  useEffect(() => {
    const checkAndRedirect = async () => {
      if (!authLoading && !roleLoading && !userLoading) {
        const isPublicRoute = publicRoutes.includes(location.pathname);

        if (currentUser && currentUserData && currentUserData.role === 'pending') {
          if (location.pathname !== '/account-not-activated') {
            toast.error(
              'AuthCheck: User has pending application, redirecting to account not activated page'
            );
            navigate('/account-not-activated', { replace: true });
            return;
          }
        }

        if (!currentUser && !isPublicRoute) {
          toast.error('AuthCheck: User not authenticated, redirecting to landing');
          navigate('/landing', { replace: true });
          return;
        }

        if (currentUser && isPublicRoute && userRole && currentUserData?.role !== 'pending') {
          // Check if user is a delegated user or employee first
          try {
            // Check if user is a coach delegated user
            const delegatedUserDoc = await getDoc(
              doc(firestore, 'delegatedUsers', currentUser.uid)
            );
            if (delegatedUserDoc.exists()) {
              const delegatedData = delegatedUserDoc.data();
              localStorage.setItem(
                'delegatedUser',
                JSON.stringify({
                  userId: currentUser.uid,
                  email: currentUser.email,
                  coachId: delegatedData.coachId,
                  coachEmail: delegatedData.coachEmail,
                  permissions: delegatedData.permissions,
                  userType: 'coach-delegated',
                })
              );
              navigate('/coach', { replace: true });
              return;
            }

            // Check if user is a client employee
            const clientEmployeeDoc = await getDoc(
              doc(firestore, 'clientEmployees', currentUser.uid)
            );
            if (clientEmployeeDoc.exists()) {
              const employeeData = clientEmployeeDoc.data();
              // Find the client's organization
              let organizationId = null;
              try {
                const clientSnap = await getDocs(
                  query(
                    collection(firestore, 'clients'),
                    where('email', '==', employeeData.clientEmail)
                  )
                );
                if (clientSnap.size > 0) {
                  const clientData = clientSnap.docs[0].data();
                  organizationId = clientData.organization || clientData.organizationId;
                }
              } catch (error) {
                console.error('Error finding client organization:', error);
              }

              localStorage.setItem(
                'employeeUser',
                JSON.stringify({
                  userId: currentUser.uid,
                  email: currentUser.email,
                  clientId: employeeData.clientId,
                  clientEmail: employeeData.clientEmail,
                  organizationId: organizationId,
                  permissions: employeeData.permissions,
                  userType: 'client-employee',
                })
              );
              navigate('/client', { replace: true });
              return;
            }
          } catch (error) {
            console.error('Error checking delegated/employee user status:', error);
          }

          // Normal role-based routing
          switch (userRole) {
            case 'alumni':
              navigate('/alumini', { replace: true });
              break;
            case 'coach':
            case 'businessCoach':
              navigate('/coach', { replace: true });
              break;
            case 'client':
            case 'entrepreneur':
              navigate('/client', { replace: true });
              break;
            case 'admin':
              navigate('/admin', { replace: true });
              break;
            case 'moderator':
              navigate('/moderator', { replace: true });
              break;
            case 'superAdmin':
              navigate('/super-admin', { replace: true });
              break;
            case 'PlatformEngineer':
              navigate('/PlatformEngineer', { replace: true });
              break;
            default:
              toast.error('AuthCheck: User role not determined yet');
          }
        }
      }
    };

    checkAndRedirect();
  }, [
    currentUser,
    userRole,
    authLoading,
    roleLoading,
    userLoading,
    currentUserData,
    location.pathname,
    navigate,
    publicRoutes,
  ]);

  if (authLoading || roleLoading || userLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <MarbleLoader size="md" />
      </div>
    );
  }

  return children;
};

export default AuthCheck;
